<template>
<div class="details-ctn pt-4 mr-4 pb-6">
    <!-- header with arrow icon -->
  <div class="d-flex align-center" >
    <v-icon color="black" size="24" class="mr-5 clickable" @click="goBack">mdi-arrow-left</v-icon>
    <span class="header-text" v-if="selectedProduct">{{ selectedProduct?.product_name }}</span>
  </div>

  <!-- connection status -->
  <div class="d-flex align-center mt-13" :class="isProductConnected ? 'mb-4' : 'mb-6'">
    <span class="text-status mr-5 flex-shrink-0 w-160">{{ $t('amazon_transparency_connection_status') }}</span>
    <span class="text-capitalize text-wrap" :class="isProductConnected ? 'text-connected' : 'text-failed'">
      {{ selectedProduct?.status }}
    </span>
  </div>
  <div class="d-flex align-center mb-6" v-if="isProductConnected && isAllowed('amz_transparency_edit')">
    <v-switch
      label=""
      v-model="isAutoUpload"
      class="flex-shrink-0 mr-5 w-160"
      @change="toggleSwitch"
    >
      <template v-slot:label>
        <span class="ml-4 font-weight-bold" style="color: black">{{ $t('amazon_transparency_auto_upload') }}</span>
      </template>
    </v-switch>
    <span class="text-wrap text-auto-upload">
      {{ $t('amazon_transparency_by_enabling') }}
      "{{ $t('amazon_transparency_auto_upload') }},"
      {{ $t('amazon_transparency_qrcode_auto_upload') }}
    </span>
  </div>

  <v-divider></v-divider>
  <!-- product details -->
  <div class="mt-6 mb-6">
    <v-row class="mt-6">
      <!-- first row -->
      <v-col>
        <div class="d-flex">
          <span class="mr-10 font-weight-bold text-capitalize w-80 text-bold flex-shrink-0">{{ $t('amazon_transparency_product') }}</span>
          <span class="text-wrap">{{ selectedProduct?.product_name }}</span>
        </div>
      </v-col>
      <v-col>
        <div class="d-flex">
          <span class="mr-2 font-weight-bold text-capitalize text-bold w-80 flex-shrink-0">{{ $t('amazon_transparency_sku') }}</span>
          <span class="text-wrap">{{ selectedProduct?.sku }}</span>
        </div>
      </v-col>
    </v-row>
    <!-- second row -->
    <v-row>
      <v-col>
        <div class="d-flex">
          <span class="mr-10 font-weight-bold text-capitalize text-bold w-80 flex-shrink-0">{{ $t('amazon_transparency_gtin_14') }}</span>
          <span class="text-wrap">{{ selectedProduct?.gtin }}</span>
        </div>
      </v-col>
<!--      <v-col>-->
<!--        <div class="d-flex">-->
<!--          <span class="mr-10 font-weight-bold w-145 text-bold flex-shrink-0">{{ $t('amazon_transparency_total_active_codes') }}</span>-->
<!--          <span>{{ selectedProduct?.codes || 0 }}</span>-->
<!--        </div>-->
<!--      </v-col>-->
    </v-row>

    <!-- third row -->
    <v-row>
      <v-col>
        <div class="d-flex" v-if="selectedProduct?.description">
          <span class="font-weight-bold text-capitalize text-bold w-80 mr-10 flex-shrink-0">{{ $t('amazon_transparency_remark') }}</span>
          <span class="text-break">{{ selectedProduct?.description }}</span>
        </div>
      </v-col>
    </v-row>
  </div>
  <v-divider></v-divider>

  <!-- transactions table -->
  <div class="mt-6 d-flex align-center mb-4">
    <span class="transactions-header-text">{{ $t('amazon_transparency_tbya_transactions') }}</span>
    <v-spacer></v-spacer>
    <div class="d-flex align-center justify-center">
      <v-icon color="black" size="24" class="align-self-center mr-5">mdi-filter-variant</v-icon>
      <v-select
        v-model="selectedStatus"
        :items="filterOptions"
        :item-disabled="isItemDisabled"
        label="Status"
        dense
        solo
        height="40"
        class="custom-status-select elevation-0"
      />
    </div>
  </div>

  <!-- data table -->
  <div class="mt-5">
    <v-data-table
      :headers="headers"
      :loading="isLoading"
      :items-per-page="5"
      :items="productTransactions"
      :options.sync="options"
      :server-items-length="totalProductTransactions"
      :footer-props="{
              'items-per-page-text': '',
              'disable-items-per-page': true
      }"
      class="custom-transactions-table"
      item-value="id"
      :loading-text="$t('amazon_transparency_loading_transactions')"
      hide-default-footer
    >

      <!-- source -->
      <template v-slot:item.source="{ item }">
        <span class="text-capitalize">{{ item.source}}</span>
      </template>

      <!-- status -->
      <template v-slot:item.status="{ item }">
        <span class="text-capitalize" :class="utils.getStatusStyle(item.status.toLowerCase())">
          {{ utils.transformStatusText(item.status) }}
        </span>
      </template>

      <!-- table footer  -->
      <template v-slot:footer>
        <div class="d-flex justify-end mt-5 mb-4">
          <v-pagination
            class="flex-grow-0"
            v-model="options.page"
            :length="totalPages"
            :total-visible="7"
            @click.stop
          />
        </div>
      </template>
    </v-data-table>
  </div>
  <auto-upload-confirmation-modal
    :display-confirmation-modal="showConfirmationModal"
    :handle-cancel-btn="handleCancelBtn"
    :handle-disable-btn="handleDisableBtn"
    :handle-enable-btn="handleEnableBtn"
    :is-disabling="isDisablingAutoUpload"
    />
</div>
</template>

<script>
import { mapGetters } from 'vuex'
import AutoUploadConfirmationModal from '@/components/amazon-transparency/dialog/AutoUploadConfirmationModal.vue'
import Utils from '@/utils/utils'

export default {
  name: 'ProductDetails',
  components: {
    AutoUploadConfirmationModal
  },
  data () {
    return {
      selectedStatus: 'status',
      isLoading: false,
      isAutoUpload: false,
      showConfirmationModal: false,
      isDisablingAutoUpload: false,
      filterOptions: [
        { text: this.$t('amazon_transparency_status_header'), value: null, disabled: true },
        { text: this.$t('amazon_transparency_all'), value: 'all' },
        { text: this.$t('amazon_transparency_pending'), value: 'pending' },
        { text: this.$t('amazon_transparency_failed'), value: 'failed' },
        { text: this.$t('amazon_transparency_completed'), value: 'success' }
      ],
      headers: [
        { text: this.$t('amazon_transparency_date'), align: 'center', sortable: false, value: 'created_at' },
        { text: this.$t('amazon_transparency_source'), value: 'source', sortable: false, align: 'center' },
        { text: this.$t('amazon_transparency_connected_codes'), value: 'code_count', sortable: false, align: 'center' },
        { text: this.$t('amazon_transparency_status_header'), value: 'status', sortable: false, align: 'center' }
      ],
      defaultParams: { ordering: '-created_at', limit: 5 },
      options: {
        page: 1,
        itemsPerPage: 5
      },
      getUrlParams (extraParams) {
        const merged = { ...this.defaultParams, ...extraParams }
        const params = new URLSearchParams()

        for (const key in merged) {
          if (merged[key] != null && merged[key] !== '') {
            params.append(key, merged[key])
          }
        }

        return params
      }
    }
  },
  computed: {
    utils () {
      return Utils
    },
    ...mapGetters(['selectedProduct', 'productTransactions', 'totalProductTransactions', 'isAllowed']),
    isProductConnected () {
      return this.selectedProduct?.status.toLowerCase() === 'connected'
    },
    totalPages () {
      return Math.ceil(this.totalProductTransactions / this.options.itemsPerPage)
    }
  },
  methods: {
    async fetchProductTransactions () {
      try {
        if (this.isLoading) return
        const { page = 1, itemsPerPage = 5 } = this.options
        const extraParams = {
          offset: (page - 1) * itemsPerPage,
          limit: itemsPerPage,
          search: this.selectedProduct?.sku
        }

        if (this.selectedStatus.toLowerCase() !== 'all' && this.selectedStatus.toLowerCase() !== 'status') {
          extraParams.status = this.selectedStatus
        }

        this.isLoading = true
        const payload = this.getUrlParams(extraParams)
        await this.$store.dispatch('loadProductTransactions', payload)
      } catch (error) {
        if (error?.response) {
          await this.displaySnackbar(this.$t('amazon_transparency_error_occurred'))
        } else if (error?.request) {
          await this.displaySnackbar(this.$t('amazon_transparency_no_response'))
        } else {
          await this.displaySnackbar(this.$t('amazon_transparency_error_occurred'))
        }
      } finally {
        this.isLoading = false
      }
    },
    goBack () {
      this.$router.push({ name: 'amazon-transparency.products' })
      this.$store.dispatch('setSelectedProduct', null)
      const payload = { productTransactions: [], selectedProduct: null }
      this.$store.dispatch('setProductTransactions', { ...payload })
      this.$store.dispatch('setTotalProductsTransactionsCount', 0)
    },
    isItemDisabled (item) {
      return item.disabled
    },
    toggleSwitch (val) {
      if (val) {
        this.showConfirmationModal = true
        this.isDisablingAutoUpload = false
      } else {
        this.showConfirmationModal = true
        this.isDisablingAutoUpload = true
      }
    },
    handleCancelBtn () {
      this.isAutoUpload = this.selectedProduct?.is_active
      this.showConfirmationModal = false
    },
    handleEnableBtn () {
      this.showConfirmationModal = false
      this.enableAutoUpload()
    },
    handleDisableBtn () {
      this.showConfirmationModal = false
      this.disableAutoUpload()
    },
    async disableAutoUpload () {
      try {
        const payload = { productId: this.selectedProduct.id }
        await this.$store.dispatch('deactivateATPProduct', payload)

        const snackbarPayload = { message: this.$t('amazon_transparency_product_auto_upload_disabled') }
        await this.$store.dispatch('showSnackBar', snackbarPayload)
      } catch (error) {
        console.log('error')
      }
    },
    async enableAutoUpload () {
      try {
        const payload = { productId: this.selectedProduct.id }
        await this.$store.dispatch('activateATPProduct', payload)

        const snackbarPayload = { message: this.$t('amazon_transparency_product_auto_upload_enabled') }
        await this.$store.dispatch('showSnackBar', snackbarPayload)
      } catch (error) {
        console.log('error')
      }
    }
  },
  watch: {
    options: {
      handler () {
        this.fetchProductTransactions()
      },
      deep: true
    },
    selectedStatus () {
      this.fetchProductTransactions()
    },
    selectedProduct: {
      handler (newVal) {
        this.isAutoUpload = newVal?.is_active || false
      },
      immediate: true,
      deep: true
    }
  }
}
</script>

<style lang="sass">
  @mixin text-styles($color)
    color: $color
    font-size: 14px
    font-style: normal
    font-weight: 400
    line-height: 20px
    letter-spacing: 0.25px

  .details-ctn
    max-height: 100vh
    overflow-y: auto
    -ms-overflow-style: none
    scrollbar-width: none

    &::-webkit-scrollbar
      display: none

    .clickable
      cursor: pointer

    .text-auto-upload
      color: #000 !important
      font-size: 16px !important
      font-style: normal !important
      font-weight: 400 !important
      line-height: 26px !important

    .header-text
      color: #222
      font-size: 23px
      font-style: normal
      font-weight: 400
      line-height: normal

    .transactions-header-text
      color: #000
      font-size: 18px
      font-style: normal
      font-weight: 700
      line-height: 26px

    .custom-status-select
      max-width: 137px !important
      width: 137px !important
      height: 45px !important
      max-height: 45px !important

      .v-input__append-inner
        width: 20px

      .v-input__control
        height: 45px !important
        max-height: 45px !important
        width: 100% !important

      .v-input__slot
        border: 1px solid rgba(0, 0, 0, 0.1)
        height: 100% !important
        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1) !important

      input
        height: 100% !important
        width: 100% !important

    .custom-transactions-table
      .v-data-table-header
        background-color: #f5f5f5 !important

      .v-data-table-header th
        font-weight: 700 !important
        color: rgba(0, 0, 0, 0.87) !important
        font-size: 12px !important
        line-height: 20px !important
        letter-spacing: 0.4px !important

      .v-data-footer__select
        visibility: hidden !important
        pointer-events: none !important

      .text-success
        @include text-styles(#3FC135)

      .text-info
        @include text-styles(#00AEEF)

      .text-normal
        @include text-styles(#000)

      .text-pending
        @include text-styles(#999)

      .text-danger
        @include text-styles(#FA4C5B)

  .text-prd-processing
    @include text-styles(#00AEEF)
    font-size: 18px
    font-weight: 700
    line-height: 26px

  .text-break
    word-break: break-word !important
    overflow-wrap: break-word !important

</style>
